import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../services/api';

const getErrorMessage = (error, fallbackMessage) => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallbackMessage
);

export const fetchAdminDashboard = createAsyncThunk(
  'admin/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/admin/dashboard');
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch admin dashboard'));
    }
  }
);

export const fetchAdminOrders = createAsyncThunk(
  'admin/fetchOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.status) searchParams.set('status', params.status);
      if (params.state) searchParams.set('state', params.state);
      if (params.vendor_id) searchParams.set('vendor_id', params.vendor_id);
      if (params.from) searchParams.set('from', params.from);
      if (params.to) searchParams.set('to', params.to);
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);

      const query = searchParams.toString();
      const endpoint = query ? `/admin/orders?${query}` : '/admin/orders';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch admin orders'));
    }
  }
);

export const fetchAdminOrderDetail = createAsyncThunk(
  'admin/fetchOrderDetail',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/admin/orders/${orderId}`);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch admin order detail'));
    }
  }
);

export const assignVendor = createAsyncThunk(
  'admin/assignVendor',
  async ({ orderId, vendorId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/assign_vendor`, {
        vendor_id: vendorId,
      });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to assign vendor'));
    }
  }
);

export const processRefund = createAsyncThunk(
  'admin/processRefund',
  async ({ orderId, amountCents }, { rejectWithValue }) => {
    try {
      const payload = {};
      if (typeof amountCents === 'number') payload.amount_cents = amountCents;

      const { data } = await api.post(`/admin/orders/${orderId}/refund`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to process refund'));
    }
  }
);

export const fetchAdminProducts = createAsyncThunk(
  'admin/fetchProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);

      const query = searchParams.toString();
      const endpoint = query ? `/admin/products?${query}` : '/admin/products';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch admin products'));
    }
  }
);

export const createProduct = createAsyncThunk(
  'admin/createProduct',
  async (productPayload, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/admin/products', productPayload);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create product'));
    }
  }
);

export const updateProduct = createAsyncThunk(
  'admin/updateProduct',
  async ({ productId, updates }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/admin/products/${productId}`, updates);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update product'));
    }
  }
);

export const toggleProductActive = createAsyncThunk(
  'admin/toggleProductActive',
  async ({ productId, active }, { rejectWithValue }) => {
    try {
      const payload = {};
      if (typeof active === 'boolean') payload.active = active;

      const { data } = await api.patch(`/admin/products/${productId}/toggle_active`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to toggle product status'));
    }
  }
);

export const fetchAdminVendors = createAsyncThunk(
  'admin/fetchVendors',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (['needs_review', 'active', 'suspended'].includes(params.filter)) {
        searchParams.set('filter', params.filter);
      }
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);

      const query = searchParams.toString();
      const endpoint = query ? `/admin/vendors?${query}` : '/admin/vendors';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch admin vendors'));
    }
  }
);

export const fetchAdminQuoteRequests = createAsyncThunk(
  'admin/fetchQuoteRequests',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.status) searchParams.set('status', params.status);
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);

      const query = searchParams.toString();
      const endpoint = query ? `/admin/quote_requests?${query}` : '/admin/quote_requests';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch RFQs'));
    }
  }
);

export const fetchAdminQuoteRequestDetail = createAsyncThunk(
  'admin/fetchQuoteRequestDetail',
  async (quoteRequestId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/admin/quote_requests/${quoteRequestId}`);
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch RFQ detail'));
    }
  }
);

export const fetchAdminFastDeliveryItems = createAsyncThunk(
  'admin/fetchFastDeliveryItems',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.status) searchParams.set('status', params.status);
      if (params.page) searchParams.set('page', params.page);
      const query = searchParams.toString();
      const endpoint = query ? `/admin/fast_delivery_items?${query}` : '/admin/fast_delivery_items';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch fast-delivery inventory'));
    }
  }
);

export const setAdminFastDeliveryStatus = createAsyncThunk(
  'admin/setFastDeliveryStatus',
  async ({ itemId, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/admin/fast_delivery_items/${itemId}`, { status });
      return data.fast_delivery_item || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update fast-delivery item'));
    }
  }
);

export const approveVendor = createAsyncThunk(
  'admin/approveVendor',
  async (vendorId, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/admin/vendors/${vendorId}/approve`);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to approve vendor'));
    }
  }
);

export const approveVendorCapabilities = createAsyncThunk(
  'admin/approveVendorCapabilities',
  async ({ vendorId, reviewToken }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/admin/vendors/${vendorId}/approve_capabilities`, {
        review_token: reviewToken,
      });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to approve supplier capabilities'));
    }
  }
);

export const suspendVendor = createAsyncThunk(
  'admin/suspendVendor',
  async (vendorId, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/admin/vendors/${vendorId}/suspend`);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to suspend vendor'));
    }
  }
);

export const fetchAnalytics = createAsyncThunk(
  'admin/fetchAnalytics',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.range) searchParams.set('range', params.range);

      const query = searchParams.toString();
      const endpoint = query ? `/admin/analytics?${query}` : '/admin/analytics';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch analytics'));
    }
  }
);

const initialState = {
  dashboard: null,
  orders: [],
  orderDetail: null,
  ordersMeta: null,
  products: [],
  productsMeta: null,
  vendors: [],
  vendorsMeta: null,
  quoteRequests: [],
  quoteRequestDetail: null,
  quoteRequestsMeta: null,
  fastDeliveryItems: [],
  fastDeliveryItemsMeta: null,
  analytics: null,
  loading: false,
  error: null,
};

const upsertById = (collection, candidate) => {
  if (!candidate?.id) return collection;

  const targetId = String(candidate.id);
  let found = false;

  const next = (collection || []).map((item) => {
    if (String(item?.id) === targetId) {
      found = true;
      return { ...item, ...candidate };
    }
    return item;
  });

  if (!found) next.unshift(candidate);
  return next;
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
    clearAdminOrderDetail: (state) => {
      state.orderDetail = null;
    },
    clearAdminQuoteRequestDetail: (state) => {
      state.quoteRequestDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload?.orders || [];
        state.ordersMeta = action.payload?.meta || null;
      })
      .addCase(fetchAdminOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminOrderDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetail = action.payload?.order || action.payload;
      })
      .addCase(fetchAdminOrderDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(assignVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignVendor.fulfilled, (state, action) => {
        state.loading = false;
        const order = action.payload?.order;
        if (order) {
          state.orders = upsertById(state.orders, order);
          state.orderDetail = order;
        }
      })
      .addCase(assignVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(processRefund.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processRefund.fulfilled, (state, action) => {
        state.loading = false;
        const order = action.payload?.order;
        if (order) {
          state.orders = upsertById(state.orders, order);
          state.orderDetail = order;
        }
      })
      .addCase(processRefund.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload?.products || [];
        state.productsMeta = action.payload?.meta || null;
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        const product = action.payload?.product || action.payload;
        if (product) {
          state.products = upsertById(state.products, product);
        }
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        const product = action.payload?.product || action.payload;
        if (product) {
          state.products = upsertById(state.products, product);
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(toggleProductActive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleProductActive.fulfilled, (state, action) => {
        state.loading = false;
        const product = action.payload?.product || action.payload;
        if (product) {
          state.products = upsertById(state.products, product);
        }
      })
      .addCase(toggleProductActive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.vendors = [];
        state.vendorsMeta = null;
      })
      .addCase(fetchAdminVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload?.vendors || [];
        state.vendorsMeta = action.payload?.meta || null;
      })
      .addCase(fetchAdminVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminQuoteRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminQuoteRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.quoteRequests = action.payload?.quote_requests || [];
        state.quoteRequestsMeta = action.payload?.meta || null;
      })
      .addCase(fetchAdminQuoteRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminQuoteRequestDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminQuoteRequestDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.quoteRequestDetail = action.payload;
        state.quoteRequests = upsertById(state.quoteRequests, action.payload);
      })
      .addCase(fetchAdminQuoteRequestDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminFastDeliveryItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminFastDeliveryItems.fulfilled, (state, action) => {
        state.loading = false;
        state.fastDeliveryItems = action.payload?.fast_delivery_items || [];
        state.fastDeliveryItemsMeta = action.payload?.meta || null;
      })
      .addCase(fetchAdminFastDeliveryItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(setAdminFastDeliveryStatus.fulfilled, (state, action) => {
        state.fastDeliveryItems = upsertById(state.fastDeliveryItems, action.payload);
      })
      .addCase(setAdminFastDeliveryStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(approveVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveVendor.fulfilled, (state, action) => {
        state.loading = false;
        const vendor = action.payload?.vendor || action.payload;
        if (vendor) {
          state.vendors = upsertById(state.vendors, vendor);
        }
      })
      .addCase(approveVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(approveVendorCapabilities.pending, (state) => {
        state.error = null;
      })
      .addCase(approveVendorCapabilities.fulfilled, (state, action) => {
        const vendor = action.payload?.vendor || action.payload;
        if (vendor) {
          state.vendors = upsertById(state.vendors, vendor);
        }
      })
      .addCase(approveVendorCapabilities.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(suspendVendor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(suspendVendor.fulfilled, (state, action) => {
        state.loading = false;
        const vendor = action.payload?.vendor || action.payload;
        if (vendor) {
          state.vendors = upsertById(state.vendors, vendor);
        }
      })
      .addCase(suspendVendor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAdminError, clearAdminOrderDetail, clearAdminQuoteRequestDetail } = adminSlice.actions;
export default adminSlice.reducer;
